import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';

export interface NameSuggestion {
  id: string;
  suggested_by: string;
  name: string;
  gender: 'male' | 'female' | 'neutral';
  status: 'pending' | 'approved' | 'rejected' | 'duplicate';
  is_duplicate_aware: boolean;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export function useSuggestions() {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  // Fetch user's suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('name_suggestions')
        .select('*')
        .eq('suggested_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSuggestions(data || []);
      setPendingCount((data || []).filter((s) => s.status === 'pending').length);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Check if name already exists in baby_names
  const checkNameExists = useCallback(async (name: string, gender: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('baby_names')
        .select('id')
        .ilike('name', name)
        .eq('gender', gender)
        .eq('is_active', true)
        .limit(1);

      if (error) throw error;
      return (data?.length || 0) > 0;
    } catch (err) {
      console.error('Error checking name exists:', err);
      return false;
    }
  }, []);

  // Submit a new suggestion
  const submitSuggestion = useCallback(
    async (
      name: string,
      gender: 'male' | 'female' | 'neutral',
      isDuplicateAware: boolean = false
    ): Promise<{ success: boolean; isDuplicate?: boolean; error?: string }> => {
      if (!user) return { success: false, error: 'Nicht angemeldet' };

      try {
        // Check for duplicate in baby_names
        const exists = await checkNameExists(name, gender);

        if (exists && !isDuplicateAware) {
          return { success: false, isDuplicate: true };
        }

        // Insert suggestion
        const { error } = await supabase.from('name_suggestions').insert({
          suggested_by: user.id,
          name: name.trim(),
          gender,
          is_duplicate_aware: isDuplicateAware,
        });

        if (error) throw error;

        // Refresh list
        await fetchSuggestions();

        return { success: true };
      } catch (err) {
        console.error('Error submitting suggestion:', err);
        return { success: false, error: 'Fehler beim Einreichen' };
      }
    },
    [user, checkNameExists, fetchSuggestions]
  );

  // Withdraw a pending suggestion
  const withdrawSuggestion = useCallback(async (suggestionId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Only allow withdrawing pending suggestions
      const { error } = await supabase
        .from('name_suggestions')
        .delete()
        .eq('id', suggestionId)
        .eq('suggested_by', user.id)
        .eq('status', 'pending');

      if (error) throw error;

      // Update local state
      setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
      setPendingCount(prev => Math.max(0, prev - 1));

      return true;
    } catch (err) {
      console.error('Error withdrawing suggestion:', err);
      return false;
    }
  }, [user]);

  // Stats
  const approvedCount = suggestions.filter((s) => s.status === 'approved').length;
  const rejectedCount = suggestions.filter((s) => s.status === 'rejected').length;

  return {
    suggestions,
    isLoading,
    pendingCount,
    approvedCount,
    rejectedCount,
    submitSuggestion,
    withdrawSuggestion,
    checkNameExists,
    refetch: fetchSuggestions,
  };
}
